"use client";

import React, { createContext, useContext, useState, useCallback } from "react";

/**
 * Context for managing trip row animations when status changes via SignalR
 */
interface TripAnimationContextType {
    animatingTripId: number | null;
    setAnimatingTripId: (id: number | null) => void;
    triggerAnimation: (tripId: number, duration?: number) => void;
}

const TripAnimationContext = createContext<TripAnimationContextType>({
    animatingTripId: null,
    setAnimatingTripId: () => {},
    triggerAnimation: () => {},
});

/**
 * Hook to access trip animation context
 */
export function useTripAnimation() {
    const context = useContext(TripAnimationContext);
    if (!context) {
        throw new Error(
            "useTripAnimation must be used within TripAnimationProvider"
        );
    }
    return context;
}

/**
 * Provider component for trip animations
 */
export function TripAnimationProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [animatingTripId, setAnimatingTripId] = useState<number | null>(null);

    /**
     * Trigger animation for a specific trip
     * Auto-clears after specified duration (default 30000ms = 30 seconds for extended wiggle animation)
     */
    const triggerAnimation = useCallback(
        (tripId: number, duration: number = 30000) => {
            setAnimatingTripId(tripId);
            setTimeout(() => {
                setAnimatingTripId(null);
            }, duration);
        },
        []
    );

    return (
        <TripAnimationContext.Provider
            value={{
                animatingTripId,
                setAnimatingTripId,
                triggerAnimation,
            }}
        >
            {children}
        </TripAnimationContext.Provider>
    );
}
